// Keeps in-page jumps and keyboard focus clear of the header and stuck anchor nav (WCAG 2.4.11).
class StickyOffset {
    constructor() {
        this.header = document.querySelector('.header');
        this.anchorNav = document.querySelector('.anchor-nav-wrapper');
        this.gap = 24;
    }

    init() {
        if (!this.header) {
            return;
        }

        this.setOffset();
        window.addEventListener('resize', () => this.scheduleOffset());

        if (window.ResizeObserver) {
            const observer = new ResizeObserver(() => this.setOffset());
            observer.observe(this.header);
        }

        // Web fonts can re-wrap the anchor nav's links.
        if (document.fonts) {
            document.fonts.ready.then(() => this.setOffset());
        }

        document.addEventListener('focusin', (event) => {
            // Let the browser's own focus scroll finish first.
            window.requestAnimationFrame(() => this.revealFocus(event.target));
        });
    }

    scheduleOffset() {
        if (this.pendingFrame) {
            return;
        }

        this.pendingFrame = window.requestAnimationFrame(() => {
            this.pendingFrame = null;
            this.setOffset();
        });
    }

    setOffset() {
        const headerHeight = this.header.offsetHeight;

        if (this.anchorNav) {
            this.anchorNav.style.top = `${headerHeight}px`;
        }

        const offset = headerHeight + this.getAnchorNavHeight() + this.gap;
        document.documentElement.style.setProperty('--sticky-offset', `${offset}px`);
    }

    // Measured in its taller stuck state, since that's what covers a jump target.
    getAnchorNavHeight() {
        if (!this.anchorNav) {
            return 0;
        }

        const isSticky = this.anchorNav.classList.contains('sticky');

        if (!isSticky) {
            this.anchorNav.classList.add('sticky');
        }

        const height = this.anchorNav.offsetHeight;

        if (!isSticky) {
            this.anchorNav.classList.remove('sticky');
        }

        return height;
    }

    revealFocus(element) {
        if (element !== document.activeElement || !this.isKeyboardFocus(element) || this.isInFixedLayer(element)) {
            return;
        }

        const coveredBottom = this.getCoveredBottom(element);
        const { top, bottom } = element.getBoundingClientRect();

        // Fully off-screen targets are left to the browser or an in-progress smooth scroll.
        if (top < coveredBottom && bottom > 0) {
            window.scrollBy({ top: top - coveredBottom - this.gap, behavior: 'instant' });
        }
    }

    // Scrolling the page can't move fixed layers like modals and flyouts.
    isInFixedLayer(element) {
        for (let node = element; node && node !== document.body; node = node.parentElement) {
            if (window.getComputedStyle(node).position === 'fixed') {
                return true;
            }
        }

        return false;
    }

    // The anchor nav only counts while it's actually stuck.
    getCoveredBottom(element) {
        let bottom = 0;

        [this.header, this.anchorNav].forEach((overlay) => {
            if (!overlay || overlay.contains(element)) {
                return;
            }

            const style = window.getComputedStyle(overlay);
            const rect = overlay.getBoundingClientRect();
            const isPinned = style.position === 'fixed'
                || (style.position === 'sticky' && Math.abs(rect.top - parseFloat(style.top || 0)) < 1);

            if (isPinned && rect.height > 0) {
                bottom = Math.max(bottom, rect.bottom);
            }
        });

        return bottom;
    }

    isKeyboardFocus(element) {
        try {
            return element.matches(':focus-visible');
        } catch (e) {
            return true;
        }
    }
}

export default StickyOffset;

(() => {
    const stickyOffset = new StickyOffset();
    stickyOffset.init();
})();
